/**
 * Pure helpers for the visual question flow designer.
 *
 * The canvas is a view over existing question fields:
 * - vertical order → display_order
 * - default next edge → jump_target (-1 = End, null = sequential)
 * - labeled option edges → answer_options[option] jump targets
 *
 * Design scopes:
 * - global: one license type's global questions
 * - group: one question group's questions
 * - jurisdiction: globals + assigned groups + jurisdiction questions for one
 *   jurisdiction + license type, laid out in labeled non-overlapping boxes
 */

import type {
  JurisdictionWithLicenseTypeConfigurationResponseSchema,
  NonRegistrantQuestionResponseSchema,
} from "@/hera-api/client/types.gen";
import { getAnswerOptionKeysInConfiguredOrder } from "@/lib/answer-option-order";
import {
  type DashboardQuestionType,
  buildJurisdictionAssignedQuestionGroupKeys,
  isGroupQuestionScope,
  parseCommaSeparatedQuestionGroupNames,
} from "@/lib/filter-dashboard-questions";
import {
  BOOLEAN_ANSWER_OPTION_KEYS_ORDER,
  normalizeBooleanAnswerOptions,
} from "@/lib/question-jump-logic";

export const QUESTION_FLOW_END_NODE_ID = "__question_flow_end__";
export const QUESTION_FLOW_DEFAULT_HANDLE = "default";
export const QUESTION_FLOW_OPTION_HANDLE_PREFIX = "option:";
export const QUESTION_FLOW_CONTAINER_ID_PREFIX = "container:";

export type QuestionFlowDraft = {
  id: string;
  scope: string;
  display_order: number;
  jump_target: number | null;
  answer_options: Record<string, number | null> | null;
  answer_option_keys_order: string[] | null;
  answer_type: string;
  question: string;
  license_type_id?: string | null;
  license_type_name?: string | null;
  jurisdiction_id?: string | null;
  jurisdiction_name?: string | null;
  question_group_id?: string | null;
  question_group_name?: string | null;
  display_up_front?: boolean;
  show_on_fees_payment_screen?: boolean;
};

export type QuestionFlowEdgeKind = "sequential" | "jump" | "option";

export type QuestionFlowEdgeDraft = {
  id: string;
  source: string;
  target: string;
  sourceHandle: string;
  kind: QuestionFlowEdgeKind;
  label?: string;
};

export type QuestionFlowContainerKind = "global" | "group" | "jurisdiction";

export type QuestionFlowContainer = {
  id: string;
  kind: QuestionFlowContainerKind;
  label: string;
  questionIds: string[];
  x: number;
  y: number;
  width: number;
  height: number;
  minWidth: number;
  minHeight: number;
};

export type QuestionFlowLayoutNode = {
  id: string;
  kind: "question" | "end";
  question?: QuestionFlowDraft;
  containerId?: string | null;
  /** Position relative to parent container when containerId is set; else absolute. */
  x: number;
  y: number;
};

export type QuestionFlowGraph = {
  containers: QuestionFlowContainer[];
  nodes: QuestionFlowLayoutNode[];
  edges: QuestionFlowEdgeDraft[];
};

export type QuestionFlowPatch = {
  id: string;
  scope: string;
  display_order: number;
  jump_target: number | null;
  answer_options: Record<string, number | null> | null;
};

export type QuestionFlowDesignScope =
  | { kind: "global"; licenseTypeId: string }
  | { kind: "group"; questionGroupId: string; licenseTypeId: string }
  | {
      kind: "jurisdiction";
      jurisdictionId: string;
      licenseTypeId: string;
    };

/** Which registrant screen the canvas is designing. */
export type QuestionFlowScreenMode =
  | "display_up_front"
  | "questions"
  | "fee_payment_screen";

export const QUESTION_FLOW_SCREEN_MODES: QuestionFlowScreenMode[] = [
  "display_up_front",
  "questions",
  "fee_payment_screen",
];

export const QUESTION_FLOW_SCREEN_MODE_LABELS: Record<
  QuestionFlowScreenMode,
  string
> = {
  display_up_front: "Display up front",
  questions: "Questions",
  fee_payment_screen: "Fee Payment Screen",
};

export const QUESTION_FLOW_SCREEN_MODE_EMPTY_MESSAGES: Record<
  QuestionFlowScreenMode,
  string
> = {
  display_up_front: "No display-up-front questions in this scope to design.",
  questions: "No questions in this scope to design.",
  fee_payment_screen:
    "No fee payment screen questions in this scope to design.",
};

export type QuestionFlowPosition = { x: number; y: number };

export type QuestionFlowContainerLayout = QuestionFlowPosition & {
  width?: number;
  height?: number;
};

export type QuestionFlowViewport = {
  x: number;
  y: number;
  zoom: number;
};

export type QuestionFlowPersistedLayout = {
  containers: Record<string, QuestionFlowContainerLayout>;
  nodes: Record<string, QuestionFlowPosition>;
  /** Canvas pan/zoom; restored when reopening the same design scope. */
  viewport?: QuestionFlowViewport;
};

/** Floor for initial fitView so question cards stay readable. */
export const QUESTION_FLOW_INITIAL_MIN_ZOOM = 0.9;
export const QUESTION_FLOW_DEFAULT_ZOOM = 1;

const NODE_WIDTH = 288;
const NODE_BASE_HEIGHT = 108;
const BRANCH_OPTION_ROW = 28;
const CONTAINER_PAD_X = 24;
const CONTAINER_PAD_TOP = 48;
const CONTAINER_PAD_BOTTOM = 24;
const CONTAINER_GAP_X = 48;
const CONTAINER_GAP_Y = 48;
const END_GAP_Y = 48;
const LAYER_GAP_Y = 56;
const COL_GAP_X = 56;
const HEADER_HEIGHT = 40;

function optionHandleId(optionLabel: string): string {
  return `${QUESTION_FLOW_OPTION_HANDLE_PREFIX}${optionLabel}`;
}

export function parseOptionHandleId(
  handleId: string | null | undefined,
): string | null {
  if (!handleId?.startsWith(QUESTION_FLOW_OPTION_HANDLE_PREFIX)) return null;
  return handleId.slice(QUESTION_FLOW_OPTION_HANDLE_PREFIX.length);
}

export function supportsFlowBranchHandles(answerType: string): boolean {
  return (
    answerType === "single_select" ||
    answerType === "branch_on_choice" ||
    answerType === "boolean"
  );
}

export function getFlowBranchOptionKeys(question: QuestionFlowDraft): string[] {
  if (!supportsFlowBranchHandles(question.answer_type)) return [];
  if (question.answer_type === "boolean") {
    return [...BOOLEAN_ANSWER_OPTION_KEYS_ORDER];
  }
  return getAnswerOptionKeysInConfiguredOrder(
    question.answer_options,
    question.answer_option_keys_order,
  );
}

export function getSimplifiedQuestionScope(
  scope: string,
): "global" | "group" | "jurisdiction" {
  if (scope === "global" || scope.startsWith("global")) return "global";
  if (scope === "group" || scope.startsWith("group")) return "group";
  if (scope === "jurisdiction" || scope.startsWith("jurisdiction")) {
    return "jurisdiction";
  }
  return "global";
}

export function questionFlowContainerId(
  kind: QuestionFlowContainerKind,
  key = "default",
): string {
  return `${QUESTION_FLOW_CONTAINER_ID_PREFIX}${kind}:${key}`;
}

export function isQuestionFlowContainerId(id: string): boolean {
  return id.startsWith(QUESTION_FLOW_CONTAINER_ID_PREFIX);
}

function sortQuestions(questions: QuestionFlowDraft[]): QuestionFlowDraft[] {
  return [...questions].sort(
    (a, b) =>
      a.display_order - b.display_order || a.question.localeCompare(b.question),
  );
}

function findByDisplayOrder(
  questions: QuestionFlowDraft[],
  displayOrder: number,
): QuestionFlowDraft | undefined {
  return questions.find((q) => q.display_order === displayOrder);
}

function estimateQuestionNodeHeight(question: QuestionFlowDraft): number {
  const branchCount = getFlowBranchOptionKeys(question).length;
  return (
    NODE_BASE_HEIGHT +
    (branchCount > 0 ? 24 + branchCount * BRANCH_OPTION_ROW : 0)
  );
}

/**
 * Layered layout: sequential flow stacks vertically; branch targets at the same
 * depth fan out horizontally.
 */
export function layoutBranchingPositions(
  questions: QuestionFlowDraft[],
  origin: QuestionFlowPosition = { x: 0, y: 0 },
): {
  positions: Map<string, QuestionFlowPosition>;
  width: number;
  height: number;
} {
  const sorted = sortQuestions(questions);
  const positions = new Map<string, QuestionFlowPosition>();
  if (sorted.length === 0) {
    return { positions, width: 0, height: 0 };
  }

  const edges = buildEdges(sorted).filter(
    (edge) => edge.target !== QUESTION_FLOW_END_NODE_ID,
  );
  const branchEdges = edges.filter(
    (edge) => edge.kind === "option" || edge.kind === "jump",
  );
  const branchingSources = new Set(branchEdges.map((edge) => edge.source));
  // Option/jump targets share a layer with their siblings. Sequential
  // fall-through into those targets (e.g. Yes-path → No-path) must not
  // push them onto a deeper layer or the fan collapses into a column.
  const branchTargets = new Set(branchEdges.map((edge) => edge.target));
  const layoutEdges = edges.filter((edge) => {
    if (edge.kind !== "sequential") return true;
    if (branchingSources.has(edge.source)) return false;
    if (branchTargets.has(edge.target)) return false;
    return true;
  });
  const outgoing = new Map<string, string[]>();
  const indegree = new Map<string, number>();
  for (const question of sorted) {
    outgoing.set(question.id, []);
    indegree.set(question.id, 0);
  }
  for (const edge of layoutEdges) {
    const kids = outgoing.get(edge.source);
    if (!kids || !indegree.has(edge.target)) continue;
    if (kids.includes(edge.target)) continue;
    kids.push(edge.target);
    indegree.set(edge.target, (indegree.get(edge.target) ?? 0) + 1);
  }

  const layer = new Map<string, number>();
  const queue = sorted
    .filter((question) => (indegree.get(question.id) ?? 0) === 0)
    .map((question) => question.id);
  if (queue.length === 0) {
    queue.push(sorted[0].id);
  }
  for (const id of queue) {
    layer.set(id, 0);
  }

  const visited = new Set<string>();
  while (queue.length > 0) {
    const id = queue.shift();
    if (id == null) break;
    if (visited.has(id)) continue;
    visited.add(id);
    const parentLayer = layer.get(id) ?? 0;
    for (const childId of outgoing.get(id) ?? []) {
      const nextLayer = parentLayer + 1;
      const existing = layer.get(childId);
      if (existing == null || nextLayer > existing) {
        layer.set(childId, nextLayer);
      }
      if (!visited.has(childId)) {
        queue.push(childId);
      }
    }
  }

  let fallbackLayer = Math.max(0, ...layer.values(), 0);
  for (const question of sorted) {
    if (!layer.has(question.id)) {
      fallbackLayer += 1;
      layer.set(question.id, fallbackLayer);
    }
  }

  const byLayer = new Map<number, QuestionFlowDraft[]>();
  for (const question of sorted) {
    const depth = layer.get(question.id) ?? 0;
    const row = byLayer.get(depth) ?? [];
    row.push(question);
    byLayer.set(depth, row);
  }

  let cursorY = origin.y;
  let maxRight = origin.x;
  const depths = [...byLayer.keys()].sort((a, b) => a - b);
  for (const depth of depths) {
    const row = (byLayer.get(depth) ?? []).sort(
      (a, b) =>
        a.display_order - b.display_order ||
        a.question.localeCompare(b.question),
    );
    const rowHeight = Math.max(
      ...row.map((question) => estimateQuestionNodeHeight(question)),
      NODE_BASE_HEIGHT,
    );

    let startX = origin.x;
    if (row.length === 1) {
      const parents = layoutEdges
        .filter((edge) => edge.target === row[0].id)
        .map((edge) => edge.source);
      const parentXs = parents
        .map((parentId) => positions.get(parentId)?.x)
        .filter((value): value is number => value != null);
      if (parentXs.length > 0) {
        startX =
          parentXs.reduce((sum, value) => sum + value, 0) / parentXs.length;
      }
    }

    let x = startX;
    for (const question of row) {
      positions.set(question.id, { x, y: cursorY });
      maxRight = Math.max(maxRight, x + NODE_WIDTH);
      x += NODE_WIDTH + COL_GAP_X;
    }
    cursorY += rowHeight + LAYER_GAP_Y;
  }

  return {
    positions,
    width: Math.max(0, maxRight - origin.x),
    height: Math.max(0, cursorY - origin.y - LAYER_GAP_Y),
  };
}

export function containerSizeForQuestions(questions: QuestionFlowDraft[]): {
  width: number;
  height: number;
} {
  if (questions.length === 0) {
    return { width: NODE_WIDTH + CONTAINER_PAD_X * 2, height: 120 };
  }
  const layout = layoutBranchingPositions(questions, {
    x: CONTAINER_PAD_X,
    y: CONTAINER_PAD_TOP,
  });
  return containerMinSizeForChildLayouts(
    [...layout.positions.entries()].map(([id, position]) => {
      const question = questions.find((entry) => entry.id === id);
      return {
        x: position.x,
        y: position.y,
        question,
      };
    }),
  );
}

type ContainerChildLayoutInput = {
  x: number;
  y: number;
  width?: number;
  height?: number;
  question?: QuestionFlowDraft;
};

/** Minimum container size that fits the given child layouts (current positions). */
export function containerMinSizeForChildLayouts(
  children: ContainerChildLayoutInput[],
): { width: number; height: number } {
  if (children.length === 0) {
    return { width: NODE_WIDTH + CONTAINER_PAD_X * 2, height: 120 };
  }

  let maxRight = CONTAINER_PAD_X;
  let maxBottom = CONTAINER_PAD_TOP;
  for (const child of children) {
    const childWidth = child.width ?? NODE_WIDTH;
    const childHeight =
      child.height ??
      (child.question
        ? estimateQuestionNodeHeight(child.question)
        : NODE_BASE_HEIGHT);
    maxRight = Math.max(maxRight, child.x + childWidth);
    maxBottom = Math.max(maxBottom, child.y + childHeight);
  }

  return {
    width: Math.max(
      NODE_WIDTH + CONTAINER_PAD_X * 2,
      maxRight + CONTAINER_PAD_X,
    ),
    height: Math.max(120, maxBottom + CONTAINER_PAD_BOTTOM),
  };
}

type BoxRect = {
  id: string;
  x: number;
  y: number;
  width: number;
  height: number;
};

function boxesOverlap(
  a: BoxRect,
  b: BoxRect,
  gapX: number,
  gapY: number,
): boolean {
  return (
    a.x < b.x + b.width + gapX &&
    a.x + a.width + gapX > b.x &&
    a.y < b.y + b.height + gapY &&
    a.y + a.height + gapY > b.y
  );
}

/**
 * After a box is resized or moved, push other boxes (and optional free nodes
 * like End) so nothing overlaps the resized box. Membership is unchanged —
 * only positions move.
 */
export function resolveOverlappingBoxes(
  boxes: BoxRect[],
  options: {
    primaryId?: string;
    gapX?: number;
    gapY?: number;
    maxPasses?: number;
  } = {},
): BoxRect[] {
  const gapX = options.gapX ?? CONTAINER_GAP_X;
  const gapY = options.gapY ?? CONTAINER_GAP_Y;
  const maxPasses = options.maxPasses ?? 40;
  const next = boxes.map((box) => ({ ...box }));
  const byId = new Map(next.map((box) => [box.id, box]));

  for (let pass = 0; pass < maxPasses; pass += 1) {
    let moved = false;
    for (let i = 0; i < next.length; i += 1) {
      for (let j = i + 1; j < next.length; j += 1) {
        const a = next[i];
        const b = next[j];
        if (!boxesOverlap(a, b, gapX, gapY)) continue;

        // Keep the primary (resized/dragged) box put; move the other.
        // If neither is primary, move the one that isn't "more left/top".
        let stay = a;
        let push = b;
        if (options.primaryId === b.id) {
          stay = b;
          push = a;
        } else if (options.primaryId === a.id) {
          stay = a;
          push = b;
        } else if (b.x < a.x || (b.x === a.x && b.y < a.y)) {
          stay = b;
          push = a;
        }

        const overlapRight = stay.x + stay.width + gapX - push.x;
        const overlapLeft = push.x + push.width + gapX - stay.x;
        const overlapBottom = stay.y + stay.height + gapY - push.y;
        const overlapTop = push.y + push.height + gapY - stay.y;
        const pushRight = Math.max(0, overlapRight);
        const pushLeft = Math.max(0, overlapLeft);
        const pushDown = Math.max(0, overlapBottom);
        const pushUp = Math.max(0, overlapTop);

        const candidates = [
          { dx: pushRight, dy: 0 },
          { dx: -pushLeft, dy: 0 },
          { dx: 0, dy: pushDown },
          { dx: 0, dy: -pushUp },
        ].filter((c) => c.dx !== 0 || c.dy !== 0);

        if (candidates.length === 0) continue;
        candidates.sort(
          (left, right) =>
            Math.abs(left.dx) +
            Math.abs(left.dy) -
            (Math.abs(right.dx) + Math.abs(right.dy)),
        );
        const best = candidates[0];
        push.x += best.dx;
        push.y += best.dy;
        moved = true;
      }
    }
    if (!moved) break;
  }

  return next.map((box) => {
    const original = byId.get(box.id);
    return original ? { ...original, x: box.x, y: box.y } : box;
  });
}

/**
 * Place axis-aligned boxes without overlap. Prefers saved positions; otherwise
 * packs left-to-right then wraps downward.
 */
export function layoutNonOverlappingBoxes<
  T extends { id: string; width: number; height: number },
>(
  boxes: T[],
  savedPositions: Record<string, QuestionFlowPosition> = {},
  options: {
    gapX?: number;
    gapY?: number;
    startX?: number;
    startY?: number;
  } = {},
): Array<T & { x: number; y: number }> {
  const gapX = options.gapX ?? CONTAINER_GAP_X;
  const gapY = options.gapY ?? CONTAINER_GAP_Y;
  const startX = options.startX ?? 40;
  const startY = options.startY ?? 40;

  const placed: Array<T & { x: number; y: number }> = [];
  let cursorX = startX;
  let cursorY = startY;
  let rowHeight = 0;

  const overlaps = (
    a: { x: number; y: number; width: number; height: number },
    b: { x: number; y: number; width: number; height: number },
  ) =>
    a.x < b.x + b.width + gapX &&
    a.x + a.width + gapX > b.x &&
    a.y < b.y + b.height + gapY &&
    a.y + a.height + gapY > b.y;

  for (const box of boxes) {
    const saved = savedPositions[box.id];
    let x = saved?.x ?? cursorX;
    let y = saved?.y ?? cursorY;

    if (!saved) {
      x = cursorX;
      y = cursorY;
    }

    let guard = 0;
    while (
      placed.some((other) =>
        overlaps({ x, y, width: box.width, height: box.height }, other),
      ) &&
      guard < 200
    ) {
      guard += 1;
      if (saved) {
        // Nudge down/right from the saved spot until free.
        y += gapY;
        if (guard % 20 === 0) {
          x += gapX;
          y = saved.y;
        }
      } else {
        cursorX += box.width + gapX;
        if (cursorX > startX + 1400) {
          cursorX = startX;
          cursorY += rowHeight + gapY;
          rowHeight = 0;
        }
        x = cursorX;
        y = cursorY;
      }
    }

    placed.push({ ...box, x, y });

    if (!saved) {
      rowHeight = Math.max(rowHeight, box.height);
      cursorX = x + box.width + gapX;
      if (cursorX > startX + 1400) {
        cursorX = startX;
        cursorY += rowHeight + gapY;
        rowHeight = 0;
      }
    }
  }

  return placed;
}

function buildEdges(sorted: QuestionFlowDraft[]): QuestionFlowEdgeDraft[] {
  const edges: QuestionFlowEdgeDraft[] = [];

  for (let i = 0; i < sorted.length; i += 1) {
    const question = sorted[i];
    const nextInOrder = sorted[i + 1];

    if (question.jump_target === -1) {
      edges.push({
        id: `${question.id}:default:${QUESTION_FLOW_END_NODE_ID}`,
        source: question.id,
        target: QUESTION_FLOW_END_NODE_ID,
        sourceHandle: QUESTION_FLOW_DEFAULT_HANDLE,
        kind: "jump",
        label: "Next",
      });
    } else if (question.jump_target != null) {
      const target = findByDisplayOrder(sorted, question.jump_target);
      if (target) {
        edges.push({
          id: `${question.id}:default:${target.id}`,
          source: question.id,
          target: target.id,
          sourceHandle: QUESTION_FLOW_DEFAULT_HANDLE,
          kind: "jump",
          label: "Next",
        });
      }
    } else if (nextInOrder) {
      edges.push({
        id: `${question.id}:sequential:${nextInOrder.id}`,
        source: question.id,
        target: nextInOrder.id,
        sourceHandle: QUESTION_FLOW_DEFAULT_HANDLE,
        kind: "sequential",
        label: "Then",
      });
    } else {
      edges.push({
        id: `${question.id}:sequential:${QUESTION_FLOW_END_NODE_ID}`,
        source: question.id,
        target: QUESTION_FLOW_END_NODE_ID,
        sourceHandle: QUESTION_FLOW_DEFAULT_HANDLE,
        kind: "sequential",
        label: "Then",
      });
    }

    if (!supportsFlowBranchHandles(question.answer_type)) continue;

    const options =
      question.answer_type === "boolean"
        ? normalizeBooleanAnswerOptions(question.answer_options)
        : (question.answer_options ?? {});

    for (const optionLabel of getFlowBranchOptionKeys(question)) {
      const targetOrder = options[optionLabel];
      if (targetOrder == null) continue;
      if (targetOrder === -1) {
        edges.push({
          id: `${question.id}:option:${optionLabel}:${QUESTION_FLOW_END_NODE_ID}`,
          source: question.id,
          target: QUESTION_FLOW_END_NODE_ID,
          sourceHandle: optionHandleId(optionLabel),
          kind: "option",
          label: optionLabel,
        });
        continue;
      }
      const target = findByDisplayOrder(sorted, targetOrder);
      if (!target) continue;
      edges.push({
        id: `${question.id}:option:${optionLabel}:${target.id}`,
        source: question.id,
        target: target.id,
        sourceHandle: optionHandleId(optionLabel),
        kind: "option",
        label: optionLabel,
      });
    }
  }

  return edges;
}

function containerMembership(
  question: QuestionFlowDraft,
  designScope: QuestionFlowDesignScope,
): { id: string; kind: QuestionFlowContainerKind; label: string } | null {
  const simplified = getSimplifiedQuestionScope(question.scope);
  if (designScope.kind === "global") {
    return {
      id: questionFlowContainerId("global"),
      kind: "global",
      label: "Global",
    };
  }
  if (designScope.kind === "group") {
    return {
      id: questionFlowContainerId(
        "group",
        question.question_group_id ?? "group",
      ),
      kind: "group",
      label: question.question_group_name?.trim() || "Group",
    };
  }
  if (simplified === "global") {
    return {
      id: questionFlowContainerId("global"),
      kind: "global",
      label: "Global",
    };
  }
  if (simplified === "group") {
    const key =
      question.question_group_id ??
      question.question_group_name?.trim().toLowerCase() ??
      "group";
    return {
      id: questionFlowContainerId("group", key),
      kind: "group",
      label: question.question_group_name?.trim() || "Group",
    };
  }
  // Jurisdiction-specific questions stay free on the canvas (no containing box).
  return null;
}

/**
 * Build a layout graph from questions for a design scope. Global/group questions
 * sit in labeled boxes; jurisdiction questions are free. Branch targets fan out
 * horizontally instead of stacking everything in one column.
 */
export function buildQuestionFlowGraph(
  questions: QuestionFlowDraft[],
  options: {
    designScope?: QuestionFlowDesignScope;
    persistedLayout?: QuestionFlowPersistedLayout | null;
  } = {},
): QuestionFlowGraph {
  const sorted = sortQuestions(questions);
  const designScope = options.designScope ?? {
    kind: "global" as const,
    licenseTypeId: sorted[0]?.license_type_id ?? "unknown",
  };
  const persisted = options.persistedLayout ?? null;

  const groups = new Map<
    string,
    {
      id: string;
      kind: QuestionFlowContainerKind;
      label: string;
      questions: QuestionFlowDraft[];
    }
  >();
  const freeQuestions: QuestionFlowDraft[] = [];

  for (const question of sorted) {
    const membership = containerMembership(question, designScope);
    if (!membership) {
      freeQuestions.push(question);
      continue;
    }
    const existing = groups.get(membership.id);
    if (existing) {
      existing.questions.push(question);
    } else {
      groups.set(membership.id, {
        ...membership,
        questions: [question],
      });
    }
  }

  // Stable container order: Global, then groups A–Z.
  const orderedGroups = [...groups.values()].sort((a, b) => {
    const rank = (kind: QuestionFlowContainerKind) =>
      kind === "global" ? 0 : kind === "group" ? 1 : 2;
    const rankDiff = rank(a.kind) - rank(b.kind);
    if (rankDiff !== 0) return rankDiff;
    return a.label.localeCompare(b.label);
  });

  const sized = orderedGroups.map((group) => {
    const layout = layoutBranchingPositions(group.questions, {
      x: CONTAINER_PAD_X,
      y: CONTAINER_PAD_TOP,
    });
    const childLayouts = group.questions.map((question) => {
      const auto = layout.positions.get(question.id) ?? {
        x: CONTAINER_PAD_X,
        y: CONTAINER_PAD_TOP,
      };
      const relative = persisted?.nodes?.[question.id] ?? auto;
      return {
        x: relative.x,
        y: relative.y,
        question,
      };
    });
    const minSize = containerMinSizeForChildLayouts(childLayouts);
    const saved = persisted?.containers?.[group.id];
    return {
      ...group,
      layout,
      width: Math.max(minSize.width, saved?.width ?? minSize.width),
      height: Math.max(minSize.height, saved?.height ?? minSize.height),
      minWidth: minSize.width,
      minHeight: minSize.height,
    };
  });

  const placedContainers = layoutNonOverlappingBoxes(
    sized,
    persisted?.containers ?? {},
  );

  const containers: QuestionFlowContainer[] = placedContainers.map(
    (container) => ({
      id: container.id,
      kind: container.kind,
      label: container.label,
      questionIds: container.questions.map((q) => q.id),
      x: container.x,
      y: container.y,
      width: container.width,
      height: container.height,
      minWidth: container.minWidth,
      minHeight: container.minHeight,
    }),
  );

  const nodes: QuestionFlowLayoutNode[] = [];
  for (const container of placedContainers) {
    for (const question of container.questions) {
      const auto = container.layout.positions.get(question.id) ?? {
        x: CONTAINER_PAD_X,
        y: CONTAINER_PAD_TOP,
      };
      const relative = persisted?.nodes?.[question.id] ?? auto;
      nodes.push({
        id: question.id,
        kind: "question",
        question,
        containerId: container.id,
        x: relative.x,
        y: relative.y,
      });
    }
  }

  const containersBottom = containers.reduce(
    (max, container) => Math.max(max, container.y + container.height),
    40,
  );
  const containersRight = containers.reduce(
    (max, container) => Math.max(max, container.x + container.width),
    40,
  );

  const freeOrigin = {
    x: containers.length > 0 ? containersRight + CONTAINER_GAP_X : 40,
    y: 40,
  };
  // Prefer below when there are many containers side-by-side.
  const freeOriginBelow = {
    x: 40,
    y: containersBottom + CONTAINER_GAP_Y,
  };
  const freeStart =
    containers.length > 2 || freeOrigin.x > 900 ? freeOriginBelow : freeOrigin;

  const freeLayout = layoutBranchingPositions(freeQuestions, freeStart);
  for (const question of freeQuestions) {
    const saved = persisted?.nodes?.[question.id];
    const auto = freeLayout.positions.get(question.id) ?? freeStart;
    nodes.push({
      id: question.id,
      kind: "question",
      question,
      containerId: null,
      x: saved?.x ?? auto.x,
      y: saved?.y ?? auto.y,
    });
  }

  const contentBottom = Math.max(
    containersBottom,
    ...nodes.map((node) => {
      if (node.kind !== "question" || !node.question) return 0;
      if (node.containerId) {
        const parent = containers.find((c) => c.id === node.containerId);
        return (
          (parent?.y ?? 0) + node.y + estimateQuestionNodeHeight(node.question)
        );
      }
      return node.y + estimateQuestionNodeHeight(node.question);
    }),
    40,
  );
  const endSaved = persisted?.nodes?.[QUESTION_FLOW_END_NODE_ID];
  nodes.push({
    id: QUESTION_FLOW_END_NODE_ID,
    kind: "end",
    containerId: null,
    x: endSaved?.x ?? 40,
    y: endSaved?.y ?? contentBottom + END_GAP_Y,
  });

  return {
    containers,
    nodes,
    edges: buildEdges(sorted),
  };
}

export function toQuestionFlowDrafts(
  questions: NonRegistrantQuestionResponseSchema[],
): QuestionFlowDraft[] {
  return questions
    .filter((q) => !q.is_deleted)
    .map((q) => ({
      id: q.id,
      scope: q.scope,
      display_order: q.display_order,
      jump_target: q.jump_target ?? null,
      answer_options: q.answer_options ?? null,
      answer_option_keys_order: q.answer_option_keys_order ?? null,
      answer_type: q.answer_type,
      question: q.question,
      license_type_id: q.license_type_id,
      license_type_name: q.license_type_name,
      jurisdiction_id: q.jurisdiction_id,
      jurisdiction_name: q.jurisdiction_name,
      question_group_id: q.question_group_id,
      question_group_name: q.question_group_name,
      display_up_front: q.display_up_front === true,
      show_on_fees_payment_screen: q.show_on_fees_payment_screen === true,
    }));
}

export function questionFlowScreenForQuestion(question: {
  display_up_front?: boolean | null;
  show_on_fees_payment_screen?: boolean | null;
}): QuestionFlowScreenMode {
  if (question.display_up_front === true) return "display_up_front";
  if (question.show_on_fees_payment_screen === true) {
    return "fee_payment_screen";
  }
  return "questions";
}

export function filterQuestionsForFlowScreen<
  T extends {
    display_up_front?: boolean | null;
    show_on_fees_payment_screen?: boolean | null;
  },
>(questions: T[], screenMode: QuestionFlowScreenMode): T[] {
  return questions.filter(
    (question) => questionFlowScreenForQuestion(question) === screenMode,
  );
}

export function mergeVisibleQuestionFlowDrafts(
  all: QuestionFlowDraft[],
  nextVisible: QuestionFlowDraft[],
): QuestionFlowDraft[] {
  const nextById = new Map(nextVisible.map((draft) => [draft.id, draft]));
  return all.map((draft) => nextById.get(draft.id) ?? draft);
}

function questionGroupsFieldForType(
  questionType: DashboardQuestionType,
):
  | "registration_question_groups"
  | "inspection_question_groups"
  | "deregistration_question_groups" {
  if (questionType === "inspection") return "inspection_question_groups";
  if (questionType === "deregistration")
    return "deregistration_question_groups";
  return "registration_question_groups";
}

/** Filter all questions down to the set for a design scope. */
export function selectQuestionsForDesignScope(
  questions: NonRegistrantQuestionResponseSchema[],
  designScope: QuestionFlowDesignScope,
  options: {
    questionType: DashboardQuestionType;
    jurisdictions?: JurisdictionWithLicenseTypeConfigurationResponseSchema[];
  },
): NonRegistrantQuestionResponseSchema[] {
  const { questionType, jurisdictions = [] } = options;
  const ofType = questions.filter(
    (q) => q.question_type === questionType && !q.is_deleted,
  );

  if (designScope.kind === "global") {
    return ofType.filter(
      (q) =>
        getSimplifiedQuestionScope(q.scope) === "global" &&
        q.license_type_id === designScope.licenseTypeId,
    );
  }

  if (designScope.kind === "group") {
    return ofType.filter(
      (q) =>
        isGroupQuestionScope(q.scope) &&
        q.question_group_id === designScope.questionGroupId &&
        q.license_type_id === designScope.licenseTypeId,
    );
  }

  const assignedKeys = buildJurisdictionAssignedQuestionGroupKeys(
    jurisdictions,
    {
      selectedJurisdictionIds: [designScope.jurisdictionId],
      selectedLicenseTypeIds: [designScope.licenseTypeId],
      questionType,
    },
  );

  return ofType.filter((q) => {
    if (q.license_type_id !== designScope.licenseTypeId) return false;
    const simplified = getSimplifiedQuestionScope(q.scope);
    if (simplified === "global") return true;
    if (simplified === "jurisdiction") {
      return q.jurisdiction_id === designScope.jurisdictionId;
    }
    if (simplified === "group") {
      const name = q.question_group_name?.trim();
      if (!name) return false;
      return assignedKeys.has(`${q.license_type_id}:${name.toLowerCase()}`);
    }
    return false;
  });
}

/** License types a jurisdiction is configured for, as selectable options. */
export function listLicenseTypesForJurisdiction(
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[],
  jurisdictionId: string,
): Array<{ value: string; label: string }> {
  const jurisdiction = jurisdictions.find((j) => j.id === jurisdictionId);
  if (!jurisdiction) return [];
  const options = new Map<string, string>();
  for (const config of jurisdiction.license_type_configurations ?? []) {
    if (config.deleted_at) continue;
    options.set(
      config.license_type_id,
      config.license_type_name ?? config.license_type_id,
    );
  }
  return [...options.entries()]
    .map(([value, label]) => ({ value, label }))
    .sort((a, b) => a.label.localeCompare(b.label));
}

export function jurisdictionSupportsLicenseType(
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[],
  jurisdictionId: string,
  licenseTypeId: string,
): boolean {
  return listLicenseTypesForJurisdiction(jurisdictions, jurisdictionId).some(
    (option) => option.value === licenseTypeId,
  );
}

export function listAssignedGroupNamesForJurisdiction(
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[],
  jurisdictionId: string,
  licenseTypeId: string,
  questionType: DashboardQuestionType,
): string[] {
  const field = questionGroupsFieldForType(questionType);
  const jurisdiction = jurisdictions.find((j) => j.id === jurisdictionId);
  if (!jurisdiction) return [];
  const names: string[] = [];
  const seen = new Set<string>();
  for (const config of jurisdiction.license_type_configurations ?? []) {
    if (config.deleted_at) continue;
    if (config.license_type_id !== licenseTypeId) continue;
    for (const name of parseCommaSeparatedQuestionGroupNames(config[field])) {
      const key = name.toLowerCase();
      if (seen.has(key)) continue;
      seen.add(key);
      names.push(name);
    }
  }
  return names.sort((a, b) => a.localeCompare(b));
}

/**
 * After the user finishes dragging, sort by absolute canvas Y and renumber
 * display_order by 10s, remapping any jump targets that pointed at old orders.
 */
export function reorderQuestionsByVerticalPosition(
  questions: QuestionFlowDraft[],
  positionsById: Record<string, { x: number; y: number }>,
): QuestionFlowDraft[] {
  const sorted = [...questions].sort((a, b) => {
    const ay = positionsById[a.id]?.y ?? a.display_order;
    const by = positionsById[b.id]?.y ?? b.display_order;
    if (ay !== by) return ay - by;
    return a.display_order - b.display_order;
  });

  const oldOrderToId = new Map(questions.map((q) => [q.display_order, q.id]));
  const idToNewOrder = new Map(
    sorted.map((q, index) => [q.id, (index + 1) * 10] as const),
  );

  const remapTarget = (target: number | null | undefined): number | null => {
    if (target == null) return null;
    if (target === -1) return -1;
    const targetId = oldOrderToId.get(target);
    if (!targetId) return target;
    return idToNewOrder.get(targetId) ?? target;
  };

  return sorted.map((question) => {
    const nextOptions =
      question.answer_options == null
        ? null
        : Object.fromEntries(
            Object.entries(question.answer_options).map(([key, value]) => [
              key,
              remapTarget(value),
            ]),
          );

    return {
      ...question,
      display_order: idToNewOrder.get(question.id) ?? question.display_order,
      jump_target: remapTarget(question.jump_target),
      answer_options: nextOptions,
    };
  });
}

/**
 * Apply a connection from a source handle to a target question or End.
 * Passing targetId null clears that handle (restore sequential default).
 */
export function applyQuestionFlowConnection(
  questions: QuestionFlowDraft[],
  sourceId: string,
  sourceHandle: string,
  targetId: string | null,
): QuestionFlowDraft[] {
  const byId = new Map(questions.map((q) => [q.id, q]));
  const source = byId.get(sourceId);
  if (!source) return questions;

  let targetOrder: number | null = null;
  if (targetId === QUESTION_FLOW_END_NODE_ID) {
    targetOrder = -1;
  } else if (targetId != null) {
    const target = byId.get(targetId);
    if (!target) return questions;
    targetOrder = target.display_order;
  }

  const optionLabel = parseOptionHandleId(sourceHandle);
  if (optionLabel != null) {
    const baseOptions =
      source.answer_type === "boolean"
        ? normalizeBooleanAnswerOptions(source.answer_options)
        : { ...(source.answer_options ?? {}) };
    return questions.map((q) => {
      if (q.id !== sourceId) return q;
      return {
        ...q,
        answer_options: {
          ...baseOptions,
          [optionLabel]: targetOrder,
        },
      };
    });
  }

  // Default next handle
  return questions.map((q) => {
    if (q.id !== sourceId) return q;
    return {
      ...q,
      jump_target: targetOrder,
    };
  });
}

/** Patches to persist for questions whose flow fields changed. */
export function diffQuestionFlowPatches(
  original: QuestionFlowDraft[],
  next: QuestionFlowDraft[],
): QuestionFlowPatch[] {
  const originalById = new Map(original.map((q) => [q.id, q]));
  const patches: QuestionFlowPatch[] = [];

  for (const question of next) {
    const before = originalById.get(question.id);
    if (!before) continue;
    const optionsChanged =
      JSON.stringify(before.answer_options ?? null) !==
      JSON.stringify(question.answer_options ?? null);
    if (
      before.display_order === question.display_order &&
      before.jump_target === question.jump_target &&
      !optionsChanged
    ) {
      continue;
    }
    patches.push({
      id: question.id,
      scope: question.scope,
      display_order: question.display_order,
      jump_target: question.jump_target,
      answer_options: question.answer_options,
    });
  }

  return patches;
}

export function truncateQuestionLabel(text: string, max = 72): string {
  const compact = text.replace(/\s+/g, " ").trim();
  if (compact.length <= max) return compact;
  return `${compact.slice(0, max - 1)}…`;
}

export function questionFlowLayoutStorageKey(
  questionType: DashboardQuestionType,
  designScope: QuestionFlowDesignScope,
  screenMode: QuestionFlowScreenMode = "questions",
): string {
  const suffix =
    screenMode === "display_up_front"
      ? ":up-front"
      : screenMode === "fee_payment_screen"
        ? ":fee-payment"
        : "";
  if (designScope.kind === "global") {
    return `question-flow-layout:${questionType}:global:${designScope.licenseTypeId}${suffix}`;
  }
  if (designScope.kind === "group") {
    return `question-flow-layout:${questionType}:group:${designScope.licenseTypeId}:${designScope.questionGroupId}${suffix}`;
  }
  return `question-flow-layout:${questionType}:jurisdiction:${designScope.jurisdictionId}:${designScope.licenseTypeId}${suffix}`;
}

function parsePersistedViewport(
  value: unknown,
): QuestionFlowViewport | undefined {
  if (!value || typeof value !== "object") return undefined;
  const candidate = value as Partial<QuestionFlowViewport>;
  if (
    typeof candidate.x !== "number" ||
    typeof candidate.y !== "number" ||
    typeof candidate.zoom !== "number" ||
    !Number.isFinite(candidate.x) ||
    !Number.isFinite(candidate.y) ||
    !Number.isFinite(candidate.zoom) ||
    candidate.zoom <= 0
  ) {
    return undefined;
  }
  return { x: candidate.x, y: candidate.y, zoom: candidate.zoom };
}

export function loadQuestionFlowPersistedLayout(
  storageKey: string,
): QuestionFlowPersistedLayout | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(storageKey);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QuestionFlowPersistedLayout;
    if (!parsed || typeof parsed !== "object") return null;
    const viewport = parsePersistedViewport(parsed.viewport);
    return {
      containers: parsed.containers ?? {},
      nodes: parsed.nodes ?? {},
      ...(viewport ? { viewport } : {}),
    };
  } catch {
    return null;
  }
}

export function saveQuestionFlowPersistedLayout(
  storageKey: string,
  layout: QuestionFlowPersistedLayout,
): void {
  if (typeof window === "undefined") return;
  try {
    const existing = loadQuestionFlowPersistedLayout(storageKey);
    const viewport =
      parsePersistedViewport(layout.viewport) ??
      parsePersistedViewport(existing?.viewport);
    const merged: QuestionFlowPersistedLayout = {
      containers: layout.containers,
      nodes: layout.nodes,
      ...(viewport ? { viewport } : {}),
    };
    window.localStorage.setItem(storageKey, JSON.stringify(merged));
  } catch {
    // Ignore quota / serialization errors.
  }
}

/** Update only the saved viewport for a layout key (merge with existing layout). */
export function saveQuestionFlowViewport(
  storageKey: string,
  viewport: QuestionFlowViewport,
): void {
  if (typeof window === "undefined") return;
  const parsed = parsePersistedViewport(viewport);
  if (!parsed) return;
  const existing = loadQuestionFlowPersistedLayout(storageKey);
  saveQuestionFlowPersistedLayout(storageKey, {
    containers: existing?.containers ?? {},
    nodes: existing?.nodes ?? {},
    viewport: parsed,
  });
}

export function extractPersistedLayoutFromNodes(
  nodes: Array<{
    id: string;
    position: QuestionFlowPosition;
    parentId?: string;
    width?: number;
    height?: number;
    style?: { width?: number | string; height?: number | string };
    data?: { width?: number; height?: number };
  }>,
  viewport?: QuestionFlowViewport | null,
): QuestionFlowPersistedLayout {
  const containers: Record<string, QuestionFlowContainerLayout> = {};
  const nodePositions: Record<string, QuestionFlowPosition> = {};
  for (const node of nodes) {
    if (isQuestionFlowContainerId(node.id)) {
      const width = readNodeSize(node, "width");
      const height = readNodeSize(node, "height");
      containers[node.id] = {
        ...node.position,
        ...(width != null ? { width } : {}),
        ...(height != null ? { height } : {}),
      };
    } else {
      // Persist both free-node absolute positions and child positions relative
      // to their containing box. A graph rebuild must not undo a manual drag.
      nodePositions[node.id] = { ...node.position };
    }
  }
  const parsedViewport = parsePersistedViewport(viewport ?? undefined);
  return {
    containers,
    nodes: nodePositions,
    ...(parsedViewport ? { viewport: parsedViewport } : {}),
  };
}

/**
 * While dragging a parent box, React Flow's drag snapshot can strip or rewrite
 * child `parentId` / relative positions. Merge the dragged container (and other
 * top-level moves) onto the previous node list so nested questions stay put
 * relative to their parent.
 */
export function mergeContainerDragNodes<
  T extends {
    id: string;
    position: QuestionFlowPosition;
    parentId?: string;
    expandParent?: boolean;
  },
>(previous: T[], dragged: T, dragSnapshot: T[]): T[] {
  const prevById = new Map(previous.map((node) => [node.id, node]));
  const snapshotById = new Map(dragSnapshot.map((node) => [node.id, node]));

  return previous.map((prev) => {
    if (prev.id === dragged.id) {
      return {
        ...prev,
        ...dragged,
        position: { ...dragged.position },
        // Containers are never nested.
        parentId: undefined,
      };
    }
    if (prev.parentId) {
      // Keep relative child geometry from React state, not the drag snapshot.
      return prev;
    }
    const fromSnapshot = snapshotById.get(prev.id);
    if (!fromSnapshot) return prev;
    const restoredParent = prevById.get(prev.id);
    return {
      ...prev,
      ...fromSnapshot,
      position: { ...fromSnapshot.position },
      parentId: restoredParent?.parentId,
      expandParent: restoredParent?.expandParent ?? fromSnapshot.expandParent,
    };
  });
}

/**
 * While dragging a question, React Flow's drag snapshot can strip `parentId`
 * or rewrite sibling positions. Keep every other node from previous state and
 * only apply the dragged node's new position.
 */
export function mergeDraggedNodeOntoPrevious<
  T extends {
    id: string;
    position: QuestionFlowPosition;
    parentId?: string;
    expandParent?: boolean;
    type?: string;
    data?: unknown;
  },
>(previous: T[], dragged: T): T[] {
  return previous.map((prev) => {
    if (prev.id !== dragged.id) return prev;
    return {
      ...prev,
      ...dragged,
      position: { ...dragged.position },
      parentId: prev.parentId,
      expandParent: prev.expandParent,
      type: prev.type,
      data: prev.data,
    };
  });
}

function readNodeSize(
  node: {
    width?: number;
    height?: number;
    style?: { width?: number | string; height?: number | string };
    data?: { width?: number; height?: number; question?: QuestionFlowDraft };
  },
  key: "width" | "height",
): number | null {
  const direct = node[key];
  if (typeof direct === "number" && Number.isFinite(direct)) return direct;
  const fromData = node.data?.[key];
  if (typeof fromData === "number" && Number.isFinite(fromData))
    return fromData;
  const fromStyle = node.style?.[key];
  if (typeof fromStyle === "number" && Number.isFinite(fromStyle))
    return fromStyle;
  if (typeof fromStyle === "string") {
    const parsed = Number.parseFloat(fromStyle);
    if (Number.isFinite(parsed)) return parsed;
  }
  return null;
}

/**
 * Grow each container so all of its child question nodes fit, then push
 * overlapping containers apart. Child membership is unchanged.
 */
export function fitContainersToChildNodes<
  T extends {
    id: string;
    type?: string;
    position: QuestionFlowPosition;
    parentId?: string;
    width?: number;
    height?: number;
    style?: { width?: number | string; height?: number | string };
    data?: {
      width?: number;
      height?: number;
      minWidth?: number;
      minHeight?: number;
      question?: QuestionFlowDraft;
    };
  },
>(nodes: T[], primaryContainerId?: string): T[] {
  const next = nodes.map((node) => ({ ...node }));

  for (const container of next) {
    if (!isQuestionFlowContainerId(container.id)) continue;
    const children = next.filter((node) => node.parentId === container.id);
    if (children.length === 0) continue;

    const minLeft = Math.min(...children.map((child) => child.position.x));
    const minTop = Math.min(...children.map((child) => child.position.y));
    // Normalize children so their top-left bounds sit at container padding.
    // This supports both growth (negative child positions) and shrink
    // (children previously left with large internal offsets).
    const shiftX = CONTAINER_PAD_X - minLeft;
    const shiftY = CONTAINER_PAD_TOP - minTop;

    if (shiftX !== 0 || shiftY !== 0) {
      container.position = {
        x: container.position.x - shiftX,
        y: container.position.y - shiftY,
      };
      for (const child of children) {
        child.position = {
          x: child.position.x + shiftX,
          y: child.position.y + shiftY,
        };
      }
    }

    const childLayouts = children.map((child) => ({
      x: child.position.x,
      y: child.position.y,
      width: readNodeSize(child, "width") ?? undefined,
      height: readNodeSize(child, "height") ?? undefined,
      question: child.data?.question,
    }));
    const requiredMin = containerMinSizeForChildLayouts(childLayouts);
    const currentWidth = readNodeSize(container, "width") ?? requiredMin.width;
    const currentHeight =
      readNodeSize(container, "height") ?? requiredMin.height;
    // Grow when children extend past the top-left padding; otherwise fit exactly
    // to current child bounds so boxes shrink after children move inward.
    const width =
      shiftX > 0
        ? Math.max(currentWidth + shiftX, requiredMin.width)
        : requiredMin.width;
    const height =
      shiftY > 0
        ? Math.max(currentHeight + shiftY, requiredMin.height)
        : requiredMin.height;

    container.width = width;
    container.height = height;
    container.style = { ...container.style, width, height };
    container.data = {
      ...container.data,
      width,
      height,
      minWidth: requiredMin.width,
      minHeight: requiredMin.height,
    };
  }

  return applyOverlapResolutionToFlowNodes(next, primaryContainerId) as T[];
}

/** Refresh container min sizes from current child positions without resizing boxes. */
export function refreshContainerMinSizesFromChildren<
  T extends {
    id: string;
    position: QuestionFlowPosition;
    parentId?: string;
    width?: number;
    height?: number;
    style?: { width?: number | string; height?: number | string };
    data?: {
      width?: number;
      height?: number;
      minWidth?: number;
      minHeight?: number;
      question?: QuestionFlowDraft;
    };
  },
>(nodes: T[]): T[] {
  const next = nodes.map((node) => ({ ...node }));

  for (const container of next) {
    if (!isQuestionFlowContainerId(container.id)) continue;
    const children = next.filter((node) => node.parentId === container.id);
    if (children.length === 0) continue;

    const requiredMin = containerMinSizeForChildLayouts(
      children.map((child) => ({
        x: child.position.x,
        y: child.position.y,
        width: readNodeSize(child, "width") ?? undefined,
        height: readNodeSize(child, "height") ?? undefined,
        question: child.data?.question,
      })),
    );

    container.data = {
      ...container.data,
      minWidth: requiredMin.width,
      minHeight: requiredMin.height,
    };
  }

  return next;
}

/**
 * Apply overlap resolution to top-level React Flow nodes. Container children
 * keep their parentId; boxes, free questions, and End accommodate the primary
 * box while it is moving or growing.
 */
export function applyOverlapResolutionToFlowNodes<
  T extends {
    id: string;
    type?: string;
    position: QuestionFlowPosition;
    parentId?: string;
    width?: number;
    height?: number;
    style?: { width?: number | string; height?: number | string };
    data?: {
      width?: number;
      height?: number;
      question?: QuestionFlowDraft;
    };
  },
>(nodes: T[], primaryId?: string): T[] {
  const boxes = nodes
    .filter((node) => !node.parentId)
    .map((node) => {
      const isEnd = node.id === QUESTION_FLOW_END_NODE_ID;
      const isContainer = isQuestionFlowContainerId(node.id);
      const width =
        readNodeSize(node, "width") ??
        (isEnd ? 160 : isContainer ? 320 : NODE_WIDTH);
      const height =
        readNodeSize(node, "height") ??
        (isEnd
          ? 64
          : isContainer
            ? 200
            : node.data?.question
              ? estimateQuestionNodeHeight(node.data.question)
              : NODE_BASE_HEIGHT);
      return {
        id: node.id,
        x: node.position.x,
        y: node.position.y,
        width,
        height,
      };
    });

  if (boxes.length < 2) return nodes;

  const resolved = resolveOverlappingBoxes(boxes, { primaryId });
  const byId = new Map(resolved.map((box) => [box.id, box]));

  return nodes.map((node) => {
    const box = byId.get(node.id);
    if (!box) return node;
    if (box.x === node.position.x && box.y === node.position.y) return node;
    return {
      ...node,
      position: { x: box.x, y: box.y },
    };
  });
}

/**
 * Keep a free question outside all containing boxes. Membership never changes,
 * so dragging a jurisdiction question over a Global/Group box stops at the
 * nearest outside edge instead of making it look like a child of that box.
 */
export function constrainFreeNodeOutsideContainers<
  T extends {
    id: string;
    position: QuestionFlowPosition;
    parentId?: string;
    width?: number;
    height?: number;
    style?: { width?: number | string; height?: number | string };
    data?: {
      width?: number;
      height?: number;
      question?: QuestionFlowDraft;
    };
  },
>(nodes: T[], draggedId: string, gap = 12): T[] {
  const dragged = nodes.find((node) => node.id === draggedId);
  if (
    !dragged ||
    dragged.parentId ||
    isQuestionFlowContainerId(dragged.id) ||
    dragged.id === QUESTION_FLOW_END_NODE_ID
  ) {
    return nodes;
  }

  const width = readNodeSize(dragged, "width") ?? NODE_WIDTH;
  const height =
    readNodeSize(dragged, "height") ??
    (dragged.data?.question
      ? estimateQuestionNodeHeight(dragged.data.question)
      : NODE_BASE_HEIGHT);
  let position = { ...dragged.position };

  for (let pass = 0; pass < 20; pass += 1) {
    let adjusted = false;
    for (const container of nodes) {
      if (!isQuestionFlowContainerId(container.id)) continue;
      const containerWidth = readNodeSize(container, "width") ?? 320;
      const containerHeight = readNodeSize(container, "height") ?? 200;
      const overlaps =
        position.x < container.position.x + containerWidth + gap &&
        position.x + width + gap > container.position.x &&
        position.y < container.position.y + containerHeight + gap &&
        position.y + height + gap > container.position.y;
      if (!overlaps) continue;

      const candidates = [
        {
          x: container.position.x - width - gap,
          y: position.y,
        },
        {
          x: container.position.x + containerWidth + gap,
          y: position.y,
        },
        {
          x: position.x,
          y: container.position.y - height - gap,
        },
        {
          x: position.x,
          y: container.position.y + containerHeight + gap,
        },
      ].sort(
        (a, b) =>
          Math.abs(a.x - position.x) +
          Math.abs(a.y - position.y) -
          (Math.abs(b.x - position.x) + Math.abs(b.y - position.y)),
      );
      position = candidates[0];
      adjusted = true;
    }
    if (!adjusted) break;
  }

  if (position.x === dragged.position.x && position.y === dragged.position.y) {
    return nodes;
  }
  return nodes.map((node) =>
    node.id === draggedId ? { ...node, position } : node,
  );
}

/** Absolute canvas Y for reorder, accounting for parent container offset. */
export function absolutePositionsForReorder(
  nodes: Array<{
    id: string;
    position: QuestionFlowPosition;
    parentId?: string;
  }>,
): Record<string, QuestionFlowPosition> {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const absolute: Record<string, QuestionFlowPosition> = {};
  for (const node of nodes) {
    if (isQuestionFlowContainerId(node.id)) continue;
    if (node.id === QUESTION_FLOW_END_NODE_ID) continue;
    let x = node.position.x;
    let y = node.position.y;
    if (node.parentId) {
      const parent = byId.get(node.parentId);
      if (parent) {
        x += parent.position.x;
        y += parent.position.y;
      }
    }
    absolute[node.id] = { x, y };
  }
  return absolute;
}

export function designScopeLabel(designScope: QuestionFlowDesignScope): string {
  if (designScope.kind === "global") return "Global questions";
  if (designScope.kind === "group") return "Question group";
  return "Jurisdiction flow";
}

export function designScopeStorageKeyPart(
  designScope: QuestionFlowDesignScope,
): string {
  if (designScope.kind === "global") {
    return `global:${designScope.licenseTypeId}`;
  }
  if (designScope.kind === "group") {
    return `group:${designScope.licenseTypeId}:${designScope.questionGroupId}`;
  }
  return `jurisdiction:${designScope.jurisdictionId}:${designScope.licenseTypeId}`;
}

export function designScopesEqual(
  a: QuestionFlowDesignScope | null,
  b: QuestionFlowDesignScope | null,
): boolean {
  if (a == null || b == null) return a === b;
  return designScopeStorageKeyPart(a) === designScopeStorageKeyPart(b);
}

/**
 * Resolve the Design Flow scope from page filters.
 * - Exactly one jurisdiction → jurisdiction flow (globals + assigned groups)
 * - Otherwise → global questions only
 * License type comes from a single license-type filter, else the sole license
 * type present on matching questions/configs, else null (caller must pick).
 */
export function resolveQuestionFlowDesignScopeFromFilters(options: {
  questionType: DashboardQuestionType;
  selectedJurisdictionIds: string[];
  selectedLicenseTypeIds: string[];
  allQuestions: NonRegistrantQuestionResponseSchema[];
  jurisdictions: JurisdictionWithLicenseTypeConfigurationResponseSchema[];
}): {
  designScope: QuestionFlowDesignScope | null;
  licenseTypeOptions: Array<{ value: string; label: string }>;
} {
  const {
    questionType,
    selectedJurisdictionIds,
    selectedLicenseTypeIds,
    allQuestions,
    jurisdictions,
  } = options;

  const ofType = allQuestions.filter(
    (q) => q.question_type === questionType && !q.is_deleted,
  );

  const licenseTypeOptions = (() => {
    const map = new Map<string, string>();
    for (const q of ofType) {
      map.set(q.license_type_id, q.license_type_name);
    }
    if (selectedJurisdictionIds.length === 1) {
      const jurisdiction = jurisdictions.find(
        (j) => j.id === selectedJurisdictionIds[0],
      );
      for (const config of jurisdiction?.license_type_configurations ?? []) {
        if (config.deleted_at) continue;
        if (!map.has(config.license_type_id)) {
          map.set(
            config.license_type_id,
            config.license_type_name ?? config.license_type_id,
          );
        }
      }
    }
    return [...map.entries()]
      .map(([value, label]) => ({ value, label }))
      .sort((a, b) => a.label.localeCompare(b.label));
  })();

  const resolveLicenseTypeId = (): string | null => {
    if (selectedLicenseTypeIds.length === 1) {
      return selectedLicenseTypeIds[0];
    }
    if (selectedLicenseTypeIds.length > 1) {
      // Prefer intersection with available options; if still many, ask.
      const allowed = new Set(selectedLicenseTypeIds);
      const filtered = licenseTypeOptions.filter((o) => allowed.has(o.value));
      if (filtered.length === 1) return filtered[0].value;
      return null;
    }
    if (licenseTypeOptions.length === 1) return licenseTypeOptions[0].value;
    return null;
  };

  const licenseTypeId = resolveLicenseTypeId();

  if (selectedJurisdictionIds.length === 1) {
    if (!licenseTypeId) {
      return { designScope: null, licenseTypeOptions };
    }
    return {
      designScope: {
        kind: "jurisdiction",
        jurisdictionId: selectedJurisdictionIds[0],
        licenseTypeId,
      },
      licenseTypeOptions,
    };
  }

  // No single jurisdiction → global questions only
  if (!licenseTypeId) {
    return { designScope: null, licenseTypeOptions };
  }
  return {
    designScope: { kind: "global", licenseTypeId },
    licenseTypeOptions,
  };
}

export { HEADER_HEIGHT, NODE_WIDTH, CONTAINER_PAD_X, CONTAINER_PAD_TOP };
